import React from 'react';
import { Box, Button, Tooltip, CircularProgress, Divider, IconButton } from '@mui/material';
import ExpandIcon from '@mui/icons-material/Expand';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import CancelIcon from '@mui/icons-material/Cancel';
import PublishIcon from '@mui/icons-material/Publish';
import AddIcon from '@mui/icons-material/Add';
import { BaseChip } from '../MilestoneAccordion/BaseChip';
import styles from './ActionBar.styles';

interface ActionBarProps {
       hasDraftChanges: boolean;
       hasUnsavedChanges: boolean;
       groupedMilestonesLength: number;
       expandedAccordionsSize: number;
       workflowTasksCount: number;
       allTasksReleased?: boolean;
       onExpandAll: () => void;
       onCollapseAll: () => void;
       onCancel: () => void;
       onSaveDraft: () => void;
       onSubmitChanges: () => void;
       onAddEditMilestones: () => void;
       divisionSettingsAddNewUpdate: boolean
}

export const ActionBar: React.FC<ActionBarProps> = ({
       hasDraftChanges,
       hasUnsavedChanges,
       groupedMilestonesLength,
       expandedAccordionsSize,
       workflowTasksCount,
       allTasksReleased = false,
       onExpandAll,
       onCollapseAll,
       onCancel,
       onSaveDraft,
       onSubmitChanges,
       onAddEditMilestones,
       divisionSettingsAddNewUpdate,
}) => {
       const isSubmitting = workflowTasksCount > 0;
       const hasChanges = hasDraftChanges || hasUnsavedChanges;

       return (
              <Box sx={styles.controlButtons}>
                     <Box sx={styles.buttonSection}>
                            <BaseChip
                                   hasDraftChanges={hasDraftChanges}
                                   hasUnsavedChanges={hasUnsavedChanges}
                                   workflowTasksCount={workflowTasksCount}
                                   allTasksReleased={allTasksReleased}
                            />

                            <Tooltip title={hasUnsavedChanges ? "Save Draft" : "No changes to save"} arrow placement="top">
                                   <span>
                                          <IconButton
                                                 size="large"
                                                 onClick={onSaveDraft}
                                                 disabled={!hasUnsavedChanges}
                                                 sx={{
                                                        ...styles.iconButton,
                                                        ...(!hasUnsavedChanges && {
                                                               opacity: 0.5,
                                                               filter: 'grayscale(100%)',
                                                               cursor: 'not-allowed',
                                                        }),
                                                 }}
                                          >
                                                 <SaveAsIcon sx={styles.saveIcon} />
                                          </IconButton>
                                   </span>
                            </Tooltip>

                            <Divider orientation="vertical" flexItem sx={{ height: 32 }} />

                            <Tooltip title={hasUnsavedChanges ? "Cancel Changes" : "No changes to cancel"} placement="top" arrow>
                                   <span>
                                          <IconButton
                                                 size="large"
                                                 onClick={onCancel}
                                                 disabled={!hasUnsavedChanges}
                                                 sx={{
                                                        ...styles.iconButton,
                                                        ...(!hasUnsavedChanges && {
                                                               opacity: 0.5,
                                                               filter: 'grayscale(100%)',
                                                               cursor: 'not-allowed',
                                                        }),
                                                 }}
                                          >
                                                 <CancelIcon sx={styles.cancelIcon} />
                                          </IconButton>
                                   </span>
                            </Tooltip>

                            <Divider orientation="vertical" flexItem sx={{ height: 32 }} />

                            {isSubmitting ? (
                                   <Tooltip title="Submitting Changes..." placement="top" arrow>
                                          <span>
                                                 <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1,
                                                        py: 0.5,
                                                 }}>
                                                        <CircularProgress
                                                               sx={{
                                                                      color: '#4caf50 !important',
                                                                      width: '20px !important',
                                                                      height: '20px !important',
                                                                      '& .MuiCircularProgress-circle': {
                                                                             stroke: '#4caf50 !important'
                                                                      }
                                                               }}
                                                               size={20}
                                                        />
                                                 </Box>
                                          </span>
                                   </Tooltip>
                            ) : (
                                   <Tooltip title={hasChanges ? "Submit Changes" : "No changes to submit"} placement="top" arrow>
                                          <span>
                                                 <IconButton
                                                        size="large"
                                                        onClick={onSubmitChanges}
                                                        disabled={!hasChanges}
                                                        sx={{
                                                               ...styles.iconButton,
                                                               ...(!hasChanges && {
                                                                      opacity: 0.5,
                                                                      filter: 'grayscale(100%)',
                                                                      cursor: 'not-allowed',
                                                               }),
                                                        }}
                                                 >
                                                        <PublishIcon sx={styles.submitIcon} />
                                                 </IconButton>
                                          </span>
                                   </Tooltip>
                            )}

                            <Divider orientation="vertical" flexItem sx={{ height: 32 }} />

                            {divisionSettingsAddNewUpdate && (
                                   <>
                                          <Tooltip title="Add/Edit Milestones" placement="top" arrow>
                                                 <span>
                                                        <IconButton
                                                               size="large"
                                                               onClick={onAddEditMilestones}
                                                               sx={{
                                                                      ...styles.iconButton,
                                                                      color: '#005DAA',
                                                                      '&:hover': {
                                                                             backgroundColor: 'rgba(0, 93, 170, 0.04)',
                                                                      }
                                                               }}
                                                        >
                                                               <AddIcon sx={{ color: '#005DAA' }} />
                                                        </IconButton>
                                                 </span>
                                          </Tooltip>
                                          <Divider orientation="vertical" flexItem sx={{ height: 32 }} />
                                   </>
                            )}


                            <Tooltip title={expandedAccordionsSize === groupedMilestonesLength ? "Collapse All" : "Expand All"} arrow placement="top">
                                   <span>
                                          <IconButton
                                                 size="large"
                                                 onClick={expandedAccordionsSize === groupedMilestonesLength ? onCollapseAll : onExpandAll}
                                                 disabled={groupedMilestonesLength === 0}
                                                 sx={styles.iconButton}
                                          >
                                                 <ExpandIcon
                                                        sx={{
                                                               transform: expandedAccordionsSize === groupedMilestonesLength ? 'rotate(180deg)' : 'rotate(0deg)',
                                                               transition: 'transform 0.2s ease-in-out'
                                                        }}
                                                 />
                                          </IconButton>
                                   </span>
                            </Tooltip>
                     </Box>
              </Box>
       );
}; 